
import { SelectionModel } from '@angular/cdk/collections';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSliderChange } from '@angular/material/slider';
import { Title } from '@angular/platform-browser';
import { IhmLoadingIndicatorService, ToastService } from '@ihm-software/ihm-ui-common-angular-10';
import { cloneDeep } from 'lodash';
import * as moment from 'moment';
import { FlatpickrOptions, Ng2FlatpickrComponent } from 'ng2-flatpickr';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, delay, finalize } from 'rxjs/operators';

import { Station } from '../core';
import { AudioFormatEnum } from '../downloads/model/audio-format-enum';
import { ProfileModelService } from '../profile/profile-model.service';
import { SearchFiltersService } from '../search-filters/search-filters.service';
import { ConfirmationDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { Email } from '../shared/model/email.interface';
import { MyErrorStateMatcher } from '../shared/model/error-state-matcher';
import { environment } from './../../environments/environment';
import { DaysOfWeekEnum } from './../shared/model/days-week.enum';
import { Option } from './../shared/model/option';
import { TimezoneEnum } from './../shared/model/time-zone.enum';
import { SingleDropboxComponent, SingleDropboxData } from './../shared/single-dropbox/single-dropbox.component';
import { StationUtilsService } from './../shared/utils/station-utils.service';
import { AudioSubscription } from './models/audio-subscriptions.interface';
import { AudioSubscriptionService } from './audio-subscriptions.service';
import { AudioSubscriptionResponseDto } from './models/audio-subscription-response-dto.interface';
import { StationSource } from '../core/models/station-source';

declare function require(name: string);
const flatpickr = require('flatpickr');
const confirmDatePlugin = require('flatpickr/dist/plugins/confirmDate/confirmDate');

@Component({
  selector: 'app-audio-subscriptions',
  templateUrl: './audio-subscriptions.component.html',
  styleUrls: ['./audio-subscriptions.component.scss']
})
export class AudioSubscriptionsComponent implements OnInit, AfterViewInit {

  downloadOptions = [
    { name: 'Audio', value: 'AUDIO' }
  ];
  downloadOption = this.downloadOptions[0];

  formatOptions: Option<AudioFormatEnum>[] = AudioFormatEnum.getOptions();

  stationSources: SingleDropboxData[] = [];
  selectedStationSource: SingleDropboxData;
  isStationSourcesDropboxDisable = true;

  timezoneTypeOptions: Option<TimezoneEnum>[] = TimezoneEnum.getOptions();
  selectedTimezoneOption: Option<TimezoneEnum>;
  subscriptionsListTimezoneType: TimezoneEnum;

  weekDaysOptions: Option<DaysOfWeekEnum>[] = DaysOfWeekEnum.getOptions();
  weekDaysOption: Option<DaysOfWeekEnum>;

  selectedStation: SingleDropboxData;
  origStationsList: Station[] = [];

  myForm: FormGroup;


  @ViewChild('stationMulti', { static: true }) stationMulti: SingleDropboxComponent;
  @ViewChild('stationSourceMulti', { static: true }) stationSourceMulti: SingleDropboxComponent;
  @ViewChild('sliderInputBox', { static: true }) public sliderInputBox: MatSliderChange;
  @ViewChild('emailBox', { static: true }) public emailBox: ElementRef;
  @ViewChild('matLabel', { static: true }) public matLabel: ElementRef;

  // slider
  sliderValue = environment.defaultSubscriptionTime;
  sliderMax = environment.maxSubscriptionTime;

  stringWeekDays = '';
  clearBtnText = 'CLEAR FIELDS';
  subscribeBtnText = 'SUBSCRIBE';

  startDatePlaceholder = 'Start Time';
  endDatePlaceholder = 'End Time';
  defaultStartTime = moment().startOf('day').toDate();
  defaultEndTime = moment().startOf('day').add(environment.defaultSubscriptionTime, 'm').toDate();
  timeFormatForDatePicker = 'G:i:S K'; // H -> 24hrs, G-> 01~12 hrs, i -> minutes, S-> 2 digits seconds, K -> am/pm
  timeFormatForMomentJs = 'h:mm:ss a'; // h-> hours, m-> minutes, s-> seconds, a-> am/pm
  audioSubscriptionId;
  lastRun;

  disableStationsDropBox = true;


  @ViewChild('startDatePicker', { static: true })
  pickerStartDate: Ng2FlatpickrComponent;
  startDateOptions: FlatpickrOptions = {
    dateFormat: this.timeFormatForDatePicker,
    defaultDate: this.defaultStartTime,
    enableTime: true,
    noCalendar: true,
    minuteIncrement: 1,
    onChange: (date: any) => {
      this.startDateTimeCtrl.setValue(date);
      this.adjustSlider();
      this.adjustEndDate();
    }
  };

  @ViewChild('endDatePicker', { static: true })
  pickerEndDate: Ng2FlatpickrComponent;
  endDateOptions: FlatpickrOptions = {
    dateFormat: this.timeFormatForDatePicker,
    defaultDate: this.defaultEndTime,
    enableTime: true,
    noCalendar: true,
    minuteIncrement: 1,
    onChange: (date: any) => {
      const startDate = this.startDateTimeCtrl.value;
      const startMoment = moment(startDate.length ? startDate[0] : startDate);

      let endMoment = cloneDeep(moment(date.length ? date[0] : date));

      if (endMoment.isBefore(startMoment)) {
        endMoment = cloneDeep(startMoment).add(environment.defaultSubscriptionTime, 'm');
        this.pickerEndDate.flatpickr['setDate'](endMoment.toDate());
        this.toastServiceToast.showToastMessage('The end date must be after the start date.', 'Ok');
      }

      this.endDateTimeCtrl.setValue(endMoment.toDate());
      this.adjustSlider();
    }
  };

  selectable = true;
  removable = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  matcher = new MyErrorStateMatcher();

  filteredEmails: Observable<Email[]>;

  // Confirmation page
  isConfirmed = false;
  displayedStationName: string;
  displayStationSourceName: string;
  displayedDateTime: string;
  displayAudioFormatName: string;
  displayCreateEdit: string;
  selectedStationTimezone: string;

  defaultCreateDisplay = ' <b>created</b> a new subscription ';
  defaultEditDisplay = ' <b>updated</b> a subscription ';

  isSuccess = false;
  isDuplicated = false;

  // query params
  stationIdFromQueryParams: string;
  stationObjFromQueryParam: any;
  currentUserEmail: any;
  selection: SelectionModel<AudioSubscription>;

  displayedColumns = [
    'stationName',
    'stationSource',
    'startTime',
    'endTime',
    'duration',
    'timezone',
    'format',
    'days',
    'lastRun',
    'emails',
    'edit',
    'delete'
  ];

  audioSubscriptionsList: AudioSubscription[] = [];

  startDateTimeCtrl = new FormControl(null, [Validators.required]);
  endDateTimeCtrl = new FormControl(null, [Validators.required]);
  // For Email
  emailCtrl = new FormControl(null, [
    Validators.required,
    Validators.pattern(
      '^[A-Za-z0-9._%+-]+@[a-zA-Z0-9-]+\\.[A-Za-z]{1,3}[.]?[A-Za-z]{1,}$'
    )
  ]);
  stationFormCtrl = new FormControl(null, [Validators.required]);
  timeZoneCtrl = new FormControl(null, [Validators.required]);
  sliderCtrl = new FormControl(5, [Validators.required]);
  daysOfWeekCtrl = new FormControl([], [Validators.required, Validators.minLength(1)]);
  formatOptionCtrl = new FormControl(this.formatOptions[0]);
  stationSourceCtrl = new FormControl(null, [Validators.required]);
  emailCtrlArray = new FormControl([], [Validators.required, this.minLengthArray(1)]);

  constructor(
    private profileModelService: ProfileModelService,
    private cdr: ChangeDetectorRef,
    private titleService: Title,
    private searchFiltersService: SearchFiltersService,
    private audioSubscriptionService: AudioSubscriptionService,
    private loaderService: IhmLoadingIndicatorService,
    private stationUtilsService: StationUtilsService,
    private readonly toastServiceToast: ToastService,
    private dialog: MatDialog,

  ) {
    this.selection = new SelectionModel<AudioSubscription>(false, []);
    this.titleService.setTitle('SIA (Search, Index, Archive) - Subscriptions');
    this.initializeForm();
  }

  ngOnInit() {
    this.isConfirmed = false;
    this.currentUserEmail = this.profileModelService.getProfile().token;
    this.emailCtrlArray.setValue([this.currentUserEmail]);
    this.selection = new SelectionModel<AudioSubscription>(true, []);
    this.sliderInputBox.value = this.sliderValue;

    this.timeZoneCtrl.setValue(this.timezoneTypeOptions.find(opt => opt.value === this.profileModelService.getDefaultTimezoneType()));
    this.subscriptionsListTimezoneType = this.timeZoneCtrl.value.value;
    this.startDateTimeCtrl.setValue(this.defaultStartTime);
    this.endDateTimeCtrl.setValue(this.defaultEndTime);
    this.displayCreateEdit = this.defaultCreateDisplay;

    this.loadScreen();
  }

  back() {
    this.isConfirmed = !this.isConfirmed;
    this.clear();
  }

  ngAfterViewInit() {
    this.cdr.markForCheck();
  }

  onSliderChange(event: MatSliderChange) {
    this.sliderCtrl.setValue(event.value);
    this.sliderValue = event.value;
    this.sliderInputBox.value = this.sliderValue;
    this.adjustEndDate();
  }


  public loadStations(stations) {
    this.disableStationsDropBox = false;
    this.origStationsList = stations;
    this.stationMulti.setList(
      stations.map((station) => {
        const name: string = station.name || '';
        const frequency: string = station.frequency || '';
        return {
          id: station.id,
          name: `${name} ${frequency}`,
          model: station
        };
      })
    );

  }

  getStringWeekDays() {
    let days = this.weekDaysOptions.filter(opt => opt.selected).map(opt => opt.name);
    if (days.length === 1) {
      return '<b>' + days[0] + '</b>';
    }
    days = days.map(day => day.substring(0, 3));
    return '<b>' + days.slice(0, -1).join(', ') + '</b> and <b>' + days.slice(-1) + '</b>';
  }

  submit() {
    this.displayedStationName = this.selectedStation.name;
    this.displayStationSourceName = this.selectedStationSource.name;
    this.stringWeekDays = this.getStringWeekDays();
    this.displayedDateTime = moment(this.getDate(this.startDateTimeCtrl.value), this.timeFormatForMomentJs)
      .format(this.timeFormatForMomentJs).toUpperCase();
    this.displayAudioFormatName = this.formatOptionCtrl.value.name === 'Audio MP3' ? 'MP3 Audio' : 'AAC Audio';

    const startDateTimeStr = this.getTimeString(this.startDateTimeCtrl.value);
    const endDateTimeStr = this.getTimeString(this.endDateTimeCtrl.value);

    const stationId = this.getSelectedStationId();
    this.audioSubscriptionService
      .postAudioSubscriptions({
        id: this.audioSubscriptionId,
        stationId,
        startTime: startDateTimeStr,
        endTime: endDateTimeStr,
        emails: this.emailCtrlArray.value,
        userId: this.currentUserEmail,
        audioFormat: this.formatOptionCtrl.value.value,
        daysOfWeek: this.daysOfWeekCtrl.value,
        timezone: this.selectedStationTimezone,
        lastRun: this.lastRun,
        stationName: this.selectedStation.model[0].name,
      })
      .toPromise()
      .then((subscription) => {
        if (subscription) {
          this.isSuccess = true;
        } else {
          this.isDuplicated = true;
        }

        this.isConfirmed = true;

      })
      .catch((e) => {
        this.isConfirmed = true;
        this.isSuccess = false;
        this.isDuplicated = false;
        console.error('Error occurred:', e);
      });


  }

  showEmails(subscription: AudioSubscription) {
    this.selection.select(subscription);
    this.createConfirmationDialog(subscription, 'Registered emails', 'OK', true)
      .afterClosed()
      .subscribe(result => {
        if (!this.audioSubscriptionId || subscription.id !== this.audioSubscriptionId) {
          this.selection.clear();
        }
      });
  }

  delete(subscription: AudioSubscription) {
    this.selection.select(subscription);
    this.createConfirmationDialog(subscription, 'Are you sure you want to remove this subscription?', 'YES', false)
      .afterClosed()
      .subscribe((result) => {
        this.clear();
        if (result) {
          this.audioSubscriptionService.deleteAudioSubscriptions(subscription.id.toString()).subscribe(() => {
            this.loadScreen();
          });
        }
      });
  }

  edit(subscription: AudioSubscription) {
    this.clear();

    this.displayCreateEdit = this.defaultEditDisplay;
    this.clearBtnText = 'CANCEL';
    this.subscribeBtnText = 'SAVE';

    this.selection.select(subscription);
    this.audioSubscriptionId = subscription.id;
    this.lastRun = subscription.lastRun;
    this.weekDaysOptions.forEach((opt) => { if (subscription.daysOfWeek.includes(opt.value)) { opt.selected = true; } });
    this.daysOfWeekCtrl.setValue(this.weekDaysOptions.filter(opt => opt.selected).map(days => days.value));

    this.startDateTimeCtrl.setValue(moment(subscription.startTime, this.timeFormatForMomentJs));
    this.pickerStartDate.flatpickr['setDate'](subscription.startTime);

    this.endDateTimeCtrl.setValue(moment(subscription.endTime, this.timeFormatForMomentJs));
    this.pickerEndDate.flatpickr['setDate'](subscription.endTime);

    this.sliderValue = subscription.duration;
    this.sliderInputBox.value = subscription.duration;
    this.sliderCtrl.setValue(subscription.duration);

    this.emailCtrlArray.setValue(cloneDeep(subscription.emails));

    const station = this.stationMulti.getAllItems().find(s => s.id.some((id: string) => id === subscription.stationId));
    this.selectStation(station);

    this.formatOptionCtrl.setValue(this.formatOptions.find(opt => opt.value === subscription.audioFormat));

    this.selectedStationSource = this.stationSources.find(stationSource => stationSource.id === subscription.stationSource.id);
  }

  clear() {
    this.formatOptionCtrl.setValue(this.formatOptions[0]);
    this.stationFormCtrl.setValue(null);
    this.stationSourceCtrl.setValue(null);
    this.daysOfWeekCtrl.setValue([]);
    this.displayCreateEdit = this.defaultCreateDisplay;

    this.sliderValue = environment.defaultSubscriptionTime;
    this.sliderCtrl.setValue(this.sliderValue);
    this.selectedStationTimezone = '';

    this.selectedStation = null;
    this.selectedStationSource = null;
    this.isStationSourcesDropboxDisable = true;
    this.audioSubscriptionId = null;
    this.lastRun = null;

    this.isDuplicated = false;
    this.isSuccess = false;
    this.isConfirmed = false;

    this.selection.clear();

    this.startDateTimeCtrl.setValue(this.defaultStartTime);
    this.pickerStartDate.flatpickr['setDate'](this.defaultStartTime);

    this.endDateTimeCtrl.setValue(this.defaultEndTime);
    this.pickerEndDate.flatpickr['setDate'](this.defaultEndTime);

    this.weekDaysOptions.forEach(opt => (opt.selected = false));
    this.emailCtrlArray.setValue([this.currentUserEmail]);
    this.timeZoneCtrl.setValue(this.timezoneTypeOptions.find(opt => opt.value === this.profileModelService.getDefaultTimezoneType()));

    this.clearBtnText = 'CLEAR FIELDS';
    this.subscribeBtnText = 'SUBSCRIBE';
  }

  getAudioSubscriptions() {
    this.loadScreen();
  }

  backToSubscriptions() {
    this.isConfirmed = false;
    this.isDuplicated = false;
    if (this.isSuccess) {
      this.loadScreen();
    }
  }

  optionSubscriptions() {
    this.emailCtrl.setValue(this.currentUserEmail);
  }

  updateDownloadListTimezone(useDefaultTimezoneChecked: boolean) {
    const userDefaultTimezone = this.profileModelService.getDefaultTimezoneType();
    // Use the default user timezone
    if (useDefaultTimezoneChecked) {
      this.subscriptionsListTimezoneType = userDefaultTimezone;
    } else {
      // Use the timezone different from default user timezone
      this.subscriptionsListTimezoneType = userDefaultTimezone === TimezoneEnum.STATION ?
        TimezoneEnum.USER : TimezoneEnum.STATION;
    }
    // To cause change detection and update the start and end time in the download table list
    this.audioSubscriptionsList = [...this.audioSubscriptionsList];
  }

  public selectStationSource(source: SingleDropboxData) {
    this.selectedStationSource = source;
    this.stationSourceCtrl.setValue(source);
    this.stationFormCtrl.setValue(this.selectedStation.mergedStation.mapStationSources.get(this.selectedStationSource.id));
  }

  selectStation(station: SingleDropboxData) {
    this.selectedStation = station;
    this.selectedStationTimezone = station.model[0].timeZone;
    this.stationFormCtrl.setValue(station.model[0]);
    this.setStationSources(station.mergedStation.stationSources);
  }

  setStationSources(stationSources: StationSource[]) {
    if (stationSources) {
      this.stationSources = stationSources.map(source => ({
        id: source.id,
        name: source.name,
        model: source
      }));
    }

    this.stationSourceMulti.setList(this.stationSources);

    if (this.stationSources && this.stationSources.length > 1) {
      this.isStationSourcesDropboxDisable = false;
    } else {
      this.isStationSourcesDropboxDisable = true;
    }

    const defaultStationSource = this.profileModelService.getDefaultStationSource();

    this.selectedStationSource = null;
    this.stationSourceCtrl.setValue(null);

    if (this.stationSources.length === 1) {
      this.selectedStationSource = this.stationSources[0];
    }

    if (defaultStationSource) {
      if (this.stationSources.find(source => source.id === defaultStationSource.id)) {
        this.selectedStationSource = {
          id: defaultStationSource.id,
          model: defaultStationSource,
          name: defaultStationSource.name
        };
      }
    }
    
    if (this.selectedStationSource) {
      this.stationFormCtrl.setValue(this.selectedStation.mergedStation.mapStationSources.get(this.selectedStationSource.id));
      this.stationSourceCtrl.setValue(this.selectedStationSource);
    }
  }

  timezoneTypeToString(tzType: TimezoneEnum): string {
    switch (tzType) {
      case TimezoneEnum.STATION:
        return 'Station Timezone';
      case TimezoneEnum.USER:
        return 'My Timezone';
      default:
        return '';
    }
  }


  addEmail(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    this.emailCtrl.setValue(value);
    if (this.emailCtrl.valid) {
      if (value) {
        this.emailCtrlArray.setValue([...this.emailCtrlArray.value, value]);
        this.emailCtrlArray.updateValueAndValidity();
      }
      event.value = '';
      this.emailCtrl.setValue(null);
    }
  }

  removeEmail(email: string): void {
    const index = this.emailCtrlArray.value.indexOf(email);
    if (index >= 0) {
      this.emailCtrlArray.value.splice(index, 1);
      this.emailCtrlArray.updateValueAndValidity();
    }
  }

  fillWeekDate(weekDays: any) {
    if (weekDays.selected) {
      this.daysOfWeekCtrl.setValue([...this.daysOfWeekCtrl.value, weekDays.value]);
    } else {
      this.daysOfWeekCtrl.setValue(this.daysOfWeekCtrl.value.filter(day => day !== weekDays.value));
    }
  }

  private minLengthArray(min: number): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => {
      if (c.value.length >= min) {
        return null;
      }

      return { minLength: true };
    };
  }

  private getDate(value: Date | Date[]): Date {
    if (value instanceof Array) {
      return value[0];
    }
    return value;

  }

  private adjustSlider() {
    const startMoment = moment(this.getDate(this.startDateTimeCtrl.value));
    if (this.endDateTimeCtrl.value) {
      const endMoment = moment(this.getDate(this.endDateTimeCtrl.value));
      if (endMoment) {

        let duration = Math.round(moment.duration(endMoment.diff(startMoment)).asMinutes());

        if (duration < 1) {
          duration = environment.defaultSubscriptionTime;
        }

        this.sliderValue = duration > this.sliderMax ? this.sliderMax : duration;
        this.sliderCtrl.setValue(this.sliderValue);
        this.sliderInputBox.value = this.sliderValue;


        if (duration > this.sliderMax) {
          this.adjustEndDate();
          this.toastServiceToast.showToastMessage('Maximum duration reached.', 'Ok');
        }
      }
    }
  }

  private createConfirmationDialog(
    subscription: AudioSubscription,
    title: string, okText: string,
    hideCancel: boolean): MatDialogRef<ConfirmationDialogComponent> {

    let days = DaysOfWeekEnum.getOptions().filter(day => subscription.daysOfWeek.includes(day.value)).map(day => day.name);

    days = days.map(day => day.substring(0, 3));

    let daysString = '<b>' + days[0] + '</b>';
    if (days.length > 1) {
      daysString = '<b>' + days.slice(0, -1).join(', ') + '</b> and <b>' + days.slice(-1) + '</b>';
    }

    const emails = subscription.emails;
    let emailString = '<b>' + emails[0] + '</b>';
    if (emails.length > 1) {
      emailString = '<br/>&emsp;<b>' + emails.slice(0, -1).join(',<br/>&emsp;') + '</b> and <br/>&emsp;<b>' + emails.slice(-1) + '</b>';
    }

    const cancelText = 'NO';

    let badge = '('+subscription.stationSource.name+')';

    let text = 'Station: <b>' + subscription.stationName + '</b> ' + badge + ' <br/>';

    text += 'Time: <b>' + subscription.startTime + '</b> until <b>' + subscription.endTime + '</b>';
    text += '<br/>Days: ' + daysString;
    text += '<br/>E-mails: ' + emailString;

    return this.dialog.open(ConfirmationDialogComponent, {
      width: 'auto',
      height: 'auto',
      data: { text, title, okText, cancelText, hideCancel }
    });
  }

  private adjustEndDate() {
    const date = this.startDateTimeCtrl.value;

    const startMoment = moment(date.length ? date[0] : date);

    let endMoment = cloneDeep(startMoment).add(this.sliderValue, 'm');

    if (startMoment.days() !== endMoment.days()) {
      endMoment = cloneDeep(startMoment).endOf('d');
    }

    this.endDateTimeCtrl.setValue(endMoment.toDate());
    this.pickerEndDate.flatpickr['setDate'](endMoment.toDate());
    this.adjustSlider();

  }

  private getTimeString(date: Date | Date[]): string {
    return moment(this.getDate(date)).format('HH:mm:ss');
  }

  private getSelectedStationId() {
    return this.selectedStation.mergedStation.mapStationSources.get(this.selectedStationSource.id);
  }

  private loadSubscriptions(response: AudioSubscriptionResponseDto[]) {
    this.audioSubscriptionsList = response.map(
      (r) => {
        return {
          ...r,
          stationName: this.origStationsList.find(station => station.id === r.stationId)?.name,
          isMediaMonitor: this.stationUtilsService.isSourceMediaMonitors(r.stationSource),
          isSoundPlus: this.stationUtilsService.isSourceStream(r.stationSource),
          duration: Math.round(
            moment(r.endTime, this.timeFormatForMomentJs).diff(moment(r.startTime, this.timeFormatForMomentJs), 'minutes')
          ),
          weekDays: this.getWeekDays(r.daysOfWeek),
          startTime: moment(r.startTime, this.timeFormatForMomentJs).format(this.timeFormatForMomentJs).toUpperCase(),
          endTime: moment(r.endTime, this.timeFormatForMomentJs).format(this.timeFormatForMomentJs).toUpperCase(),
          lastRunFormatted: r.lastRun ? moment(r.lastRun).format('MMM Do, YYYY') : '',
          timezone: this.origStationsList.find(station => station.id === r.stationId)?.timeZone,
        };
      }
    );

  }

  private getWeekDays(days) {
    const daysFiltered = DaysOfWeekEnum.getOptions().filter(day => days.includes(day.value)).map(day => day.name);

    return daysFiltered.map(day => day.substring(0, 3)).join(', ');
  }

  private loadScreen() {
    this.loaderService.toggle(true);
    forkJoin([
      this.searchFiltersService.loadAllStations().pipe(delay(1)).pipe(
        catchError((error) => {
          console.error('Could not list stations: ', error);
          this.toastServiceToast.showToastMessage('Sorry, could not load the stations.', 'Ok');
          return of([]);
        })
      ),
      this.audioSubscriptionService.getAudioSubscriptions(this.currentUserEmail)
        .pipe(
          catchError((error) => {
            console.error('Could not list user subscriptions: ', error);
            this.toastServiceToast.showToastMessage('Sorry, could not load your subscription\'s list.', 'Ok');
            return of([]);
          })
        )
    ]).pipe(
      finalize(() => {
        this.loaderService.toggle(false);
      }))
      .subscribe(([stations, audioSubscriptions]) => {
        this.clear();
        this.loadStations(stations);
        this.loadSubscriptions(audioSubscriptions);
      });
  }

  private initializeForm(): void {
    this.myForm = new FormGroup({
      station: this.stationFormCtrl,
      email: this.emailCtrlArray,
      startDateTime: this.startDateTimeCtrl,
      endDateTime: this.endDateTimeCtrl,
      timeZone: this.timeZoneCtrl,
      weekDays: this.daysOfWeekCtrl,
      format: this.formatOptionCtrl,
      stationSource: this.stationSourceCtrl,
    });
  }

  get TimezoneEnum() {
    return TimezoneEnum;
  }

}
